import React, { forwardRef, useCallback, useImperativeHandle, useMemo, useState } from "react";
import { Button, Card, Form, Row, Col } from "react-bootstrap";
import Spinner from "components/Spinner";
import { formatPhoneNumber } from "services/UtilityService";
import { useConfigForm } from "../configTool/useConfigForm";
import { validateCustomerCreateForm } from "../configTool/configFormValidators";
import { ConfigFormSwitch } from "../configTool/ConfigFormComponents";
import LogoUrlField, { DEFAULT_LOGO_FILE } from "../configTool/LogoUrlField";
import ConfigFormProgress from "../configTool/ConfigFormProgress";
import { useUnsavedChangesWarning } from "../configTool/useUnsavedChangesWarning";
import "./CreateCustomerForm.css";

const EMPTY_CUSTOMER_FORM = {
  customerName: "",
  programName: "",
  customerNameAbbreviation: "",
  logoUrl: DEFAULT_LOGO_FILE,
  supportNumber: "",
  faxNumberInbound: "",
  email: "",
  isAutoAcceptEnabled: false,
  isIntroFaxEnabled: false,
  showSavingPrices: true,
  botCallAllowed: false,
  botTaskAllowed: false,
  botPlanName: "",
};

const REQUIRED_FIELDS = [
  { name: "customerName", isComplete: (s) => Boolean(String(s.customerName || "").trim()) },
  { name: "programName", isComplete: (s) => Boolean(String(s.programName || "").trim()) },
  {
    name: "customerNameAbbreviation",
    isComplete: (s) => Boolean(String(s.customerNameAbbreviation || "").trim()),
  },
  { name: "logoUrl", isComplete: (s) => Boolean(String(s.logoUrl || "").trim()) },
];

const CreateCustomerForm = forwardRef(({ onSubmit, onCancel }, ref) => {
  const [loading, setLoading] = useState(false);
  const [botCallingEnabled, setBotCallingEnabled] = useState(false);

  const initialValues = useMemo(() => ({ ...EMPTY_CUSTOMER_FORM }), []);

  const validate = useCallback(
    (state) => validateCustomerCreateForm(state, { botCallingEnabled }),
    [botCallingEnabled]
  );

  const {
    formState,
    errors,
    isDirty,
    handleInputChange,
    handleCheckboxChange,
    handleBlur,
    shouldShowError,
    runValidation,
  } = useConfigForm({
    initialValues,
    validate,
  });

  const { confirmDiscard } = useUnsavedChangesWarning(isDirty);

  useImperativeHandle(ref, () => ({
    confirmDiscard,
    isDirty,
  }), [confirmDiscard, isDirty]);

  const requiredProgress = useMemo(() => {
    if (!formState) return { completed: 0, total: REQUIRED_FIELDS.length };
    const applicable = REQUIRED_FIELDS.filter(
      (f) => !f.isRequired || f.isRequired(formState)
    );
    const completed = applicable.filter((f) => f.isComplete(formState)).length;
    return { completed, total: applicable.length };
  }, [formState]);

  const handleBotCallingToggle = (event) => {
    const { checked } = event.target;
    setBotCallingEnabled(checked);
    if (!checked) {
      handleCheckboxChange({ target: { name: "botCallAllowed", checked: false } });
      handleCheckboxChange({ target: { name: "botTaskAllowed", checked: false } });
    }
  };

  const handleSubmit = () => {
    if (!runValidation()) {
      return;
    }
    setLoading(true);
    onSubmit(formState, () => setLoading(false));
  };

  const handleCancelClick = () => {
    if (!confirmDiscard()) return;
    onCancel();
  };

  if (loading || !formState) {
    return (
      <div className="d-flex justify-content-center align-items-center" style={{ height: "400px" }}>
        <Spinner />
      </div>
    );
  }

  return (
    <>
      <Form>
        <Card className="mb-3">
          <Card.Header>
            <Card.Title as="h6">Identity</Card.Title>
          </Card.Header>
          <Card.Body>
            <Row>
              <Col md={6}>
                <Form.Group>
                  <Form.Label htmlFor="customer-name">Customer Name <span className="text-danger">*</span></Form.Label>
                  <Form.Control
                    id="customer-name"
                    type="text"
                    name="customerName"
                    value={formState.customerName}
                    onChange={handleInputChange}
                    onBlur={handleBlur}
                    placeholder="e.g. Centene"
                    isInvalid={shouldShowError("customerName")}
                  />
                  {shouldShowError("customerName") && (
                    <Form.Control.Feedback type="invalid">{errors.customerName}</Form.Control.Feedback>
                  )}
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group>
                  <Form.Label htmlFor="program-name">Program Name <span className="text-danger">*</span></Form.Label>
                  <Form.Control
                    id="program-name"
                    type="text"
                    name="programName"
                    value={formState.programName}
                    onChange={handleInputChange}
                    onBlur={handleBlur}
                    placeholder="e.g. Centene RX Savings"
                    isInvalid={shouldShowError("programName")}
                  />
                  {shouldShowError("programName") && (
                    <Form.Control.Feedback type="invalid">{errors.programName}</Form.Control.Feedback>
                  )}
                </Form.Group>
              </Col>
            </Row>
            <Row className="mt-2">
              <Col md={6}>
                <Form.Group>
                  <Form.Label htmlFor="customer-abbr">Customer Abbreviation <span className="text-danger">*</span></Form.Label>
                  <Form.Control
                    id="customer-abbr"
                    type="text"
                    name="customerNameAbbreviation"
                    value={formState.customerNameAbbreviation}
                    onChange={handleInputChange}
                    onBlur={handleBlur}
                    placeholder="e.g. CNT"
                    isInvalid={shouldShowError("customerNameAbbreviation")}
                  />
                  {shouldShowError("customerNameAbbreviation") && (
                    <Form.Control.Feedback type="invalid">
                      {errors.customerNameAbbreviation}
                    </Form.Control.Feedback>
                  )}
                </Form.Group>
              </Col>
              <Col md={6}>
                <LogoUrlField
                  id="logo-url"
                  value={formState.logoUrl}
                  onChange={handleInputChange}
                  onBlur={handleBlur}
                  required
                  isInvalid={shouldShowError("logoUrl")}
                  error={errors.logoUrl}
                />
              </Col>
            </Row>
          </Card.Body>
        </Card>

        <Card className="mb-3">
          <Card.Header>
            <Card.Title as="h6">Contact</Card.Title>
          </Card.Header>
          <Card.Body>
            <Row>
              <Col md={6}>
                <Form.Group>
                  <Form.Label htmlFor="support-number">Support Number</Form.Label>
                  <Form.Control
                    id="support-number"
                    type="text"
                    name="supportNumber"
                    value={formatPhoneNumber(formState.supportNumber || "")}
                    onChange={handleInputChange}
                    onBlur={handleBlur}
                    placeholder="e.g. (555) 555-5555"
                    maxLength={14}
                    isInvalid={shouldShowError("supportNumber")}
                  />
                  {shouldShowError("supportNumber") && (
                    <Form.Control.Feedback type="invalid">{errors.supportNumber}</Form.Control.Feedback>
                  )}
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group>
                  <Form.Label htmlFor="fax-inbound">Inbound Fax Number</Form.Label>
                  <Form.Control
                    id="fax-inbound"
                    type="text"
                    name="faxNumberInbound"
                    value={formatPhoneNumber(formState.faxNumberInbound || "")}
                    onChange={handleInputChange}
                    onBlur={handleBlur}
                    placeholder="e.g. (555) 555-5555"
                    maxLength={14}
                    isInvalid={shouldShowError("faxNumberInbound")}
                  />
                  {shouldShowError("faxNumberInbound") && (
                    <Form.Control.Feedback type="invalid">{errors.faxNumberInbound}</Form.Control.Feedback>
                  )}
                </Form.Group>
              </Col>
            </Row>
            <Row className="mt-2">
              <Col md={6}>
                <Form.Group>
                  <Form.Label htmlFor="customer-email">Email</Form.Label>
                  <Form.Control
                    id="customer-email"
                    type="email"
                    name="email"
                    value={formState.email}
                    onChange={handleInputChange}
                    onBlur={handleBlur}
                    placeholder="ops@example.com"
                    isInvalid={shouldShowError("email")}
                  />
                  {shouldShowError("email") && (
                    <Form.Control.Feedback type="invalid">{errors.email}</Form.Control.Feedback>
                  )}
                </Form.Group>
              </Col>
            </Row>
          </Card.Body>
        </Card>

        <Card className="mb-3">
          <Card.Header>
            <Card.Title as="h6">Programs</Card.Title>
          </Card.Header>
          <Card.Body>
            <Row>
              <Col md={6}>
                <ConfigFormSwitch
                  label="Auto Accept"
                  fieldName="isAutoAcceptEnabled"
                  checked={formState.isAutoAcceptEnabled}
                  onChange={handleCheckboxChange}
                  id="auto-accept-switch"
                />
              </Col>
              <Col md={6}>
                <ConfigFormSwitch
                  label="Intro Fax"
                  fieldName="isIntroFaxEnabled"
                  checked={formState.isIntroFaxEnabled}
                  onChange={handleCheckboxChange}
                  id="intro-fax-switch"
                />
              </Col>
            </Row>
          </Card.Body>
        </Card>
        <Card className="mb-3">
          <Card.Header>
            <Card.Title as="h6">Automated Calling</Card.Title>
          </Card.Header>
          <Card.Body>
            <Row className="align-items-start">
              <Col md={6}>
                <ConfigFormSwitch
                  label="Bot Calling"
                  fieldName="botCallingEnabled"
                  checked={botCallingEnabled}
                  onChange={handleBotCallingToggle}
                  id="bot-calling-switch"
                />
                {botCallingEnabled && (
                  <div className="ml-4 mt-2">
                    <ConfigFormSwitch
                      label="Tasks"
                      fieldName="botTaskAllowed"
                      checked={formState.botTaskAllowed}
                      onChange={handleCheckboxChange}
                      id="bot-task-allowed-switch"
                    />
                    <ConfigFormSwitch
                      label="Calls"
                      fieldName="botCallAllowed"
                      checked={formState.botCallAllowed}
                      onChange={handleCheckboxChange}
                      id="bot-call-allowed-switch"
                    />
                    {shouldShowError("botCalling") && (
                      <div className="text-danger small mt-1">{errors.botCalling}</div>
                    )}
                  </div>
                )}
              </Col>
              {botCallingEnabled && (
                <Col md={6}>
                  <Form.Group className="d-flex align-items-center">
                    <Form.Label htmlFor="bot-plan-name" className="mb-0 mr-3 flex-shrink-0">
                      Plan Name
                    </Form.Label>
                    <Form.Control
                      id="bot-plan-name"
                      className="flex-grow-1"
                      type="text"
                      name="botPlanName"
                      value={formState.botPlanName}
                      onChange={handleInputChange}
                      onBlur={handleBlur}
                      placeholder="RxAdvocate"
                    />
                  </Form.Group>
                </Col>
              )}
            </Row>
          </Card.Body>
        </Card>
        <Card>
          <Card.Header>
            <Card.Title as="h6">Pricing</Card.Title>
          </Card.Header>
          <Card.Body>
            <Row>
              <Col md={6}>
                <ConfigFormSwitch
                  label="Show Prices"
                  fieldName="showSavingPrices"
                  checked={formState.showSavingPrices}
                  onChange={handleCheckboxChange}
                  id="show-saving-prices-switch"
                />
              </Col>
            </Row>
          </Card.Body>
        </Card>
      </Form>
      <div className="d-flex justify-content-between align-items-center flex-wrap mt-3 create-customer-form-footer">
        <ConfigFormProgress
          completed={requiredProgress.completed}
          total={requiredProgress.total}
        />
        <div className="d-flex justify-content-end mt-2 mt-md-0">
          <Button variant="info" onClick={handleCancelClick} className="mr-2">
            Cancel
          </Button>
          <Button
            variant="btn btn-primary"
            onClick={handleSubmit}
            disabled={requiredProgress.completed < requiredProgress.total}
          >
            Create Customer
          </Button>
        </div>
      </div>
    </>
  );
});

CreateCustomerForm.displayName = "CreateCustomerForm";

export default CreateCustomerForm;
